import { useState, useEffect, useRef } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useLayout } from '../../contexts/LayoutContext'
import ChatIsTyping from 'components/chat-is-typing/chat-is-typing.component'
import ChatMessage from 'components/chat-message/chat-message.component'
import ChatToolbar from 'components/chat-toolbar/chat-toolbar.component'
import UserMessage from 'components/user-message/user-message.component'
import ChatSessionManager from 'components/chat-session-manager/chat-session-manager.component'
import { Sparkles } from 'lucide-react'
import SuggestionPillsMessage from 'components/suggestion-pills-message/suggestion-pills-message.component'

import useCareerContext from 'hooks/useCareerContext'
import {
  createChatSession,
  getChatSessions,
  getChatHistory,
  deleteChatSession,
  sendChatMessage,
  getAvailableProviders,
} from 'services/chat.service'
import {
  ChatSession,
  ChatMessage as ChatMessageType,
  QuickReplyType,
  QuickReply,
  FeedbackType,
} from 'interfaces/chat.interface'
import { displayError, displaySuccess } from 'components/Toast/toast.component'
import { submitFeedback, getBatchFeedback } from 'services/feedback.service'
import {
  generateCareerWelcomeMessage,
  generateCareerSessionTopic,
  personalizeWelcomeMessage,
  needsPersonalization,
  generateFallbackWelcomeMessage,
  generateFallbackQuickReplies,
} from 'utills/careerWelcomeMessages'
import { ChatWalkthrough } from 'components/chat-walkthrough/chat-walktrhough.component'

// Helper functions to map backend quick reply data to frontend format
const mapBackendTypeToFrontend = (backendType: string): QuickReplyType => {
  switch (backendType) {
    case 'career_focus':
    case 'career_action':
      return 'career_action'
    case 'progression':
    case 'achievement':
      return 'encouragement'
    case 'expert_advice':
      return 'career_action'
    case 'exploration':
      return 'exploration'
    case 'onboarding':
      return 'onboarding'
    case 'advanced':
      return 'advanced'
    default:
      return 'exploration'
  }
}

const mapBackendActionToFrontend = (
  backendAction: string
): 'message' | 'navigation' | 'hybrid' => {
  switch (backendAction) {
    case 'navigation':
      return 'navigation'
    case 'focus_on_top_career':
    case 'review_career_history':
      return 'navigation'
    case 'go_to_careers_page':
    case 'careers_page':
      return 'navigation'
    case 'view_badges':
      return 'navigation'
    case 'available_badges':
    case 'message':
      return 'message'
    default:
      return 'message'
  }
}

const getRouteForAction = (
  backendAction: string,
  careerId?: string
): string | undefined => {
  switch (backendAction) {
    case 'focus_on_top_career':
      return careerId ? `/careers/career-insights/${careerId}` : '/careers'
    case 'review_career_history':
      return '/user-careers'
    case 'go_to_careers_page':
    case 'careers_page':
      return '/user-careers'
    case 'view_badges':
      return '/rankings'
    case 'available_badges':
      return undefined // This will be a message action
    default:
      return undefined
  }
}

const QUICK_REPLY_PREAMBLE_RE =
  /^(here\s+(are|is)|quick\s+repl|\*?\*?quick|suggestions?\s*:|options?\s*:|\d+\s*(quick|reply))/i

// Navigation chips ("Go to careers page", "View your badges", etc.) are
// disabled for now — the in-chat navigation flow is not yet wired correctly.
// Drop them in the mapper so Sherpa returns only message-type chips.
const NAV_BACKEND_ACTIONS = new Set([
  'navigation',
  'focus_on_top_career',
  'review_career_history',
  'go_to_careers_page',
  'careers_page',
  'view_badges',
])
const NAV_TEXT_RE =
  /\b(go to careers|careers page|view careers|see careers|browse careers|view (your|my) badges|earned badges|review .* careers)\b/i

const isPreambleString = (text: string): boolean => {
  if (!text) return true
  if (QUICK_REPLY_PREAMBLE_RE.test(text.trim())) return true
  if (text.trim().endsWith(':') && text.trim().length < 60) return true
  return false
}

const isNavigationReply = (reply: any): boolean => {
  if (typeof reply === 'string') return NAV_TEXT_RE.test(reply)
  if (!reply) return false
  if (typeof reply.action === 'string' && NAV_BACKEND_ACTIONS.has(reply.action)) {
    return true
  }
  const label = reply.text || reply.label
  return typeof label === 'string' && NAV_TEXT_RE.test(label)
}

const mapBackendQuickRepliesToFrontend = (
  backendQuickReplies: any[]
): any[] => {
  return backendQuickReplies
    .filter(reply => {
      if (isNavigationReply(reply)) return false
      const label = typeof reply === 'string' ? reply : reply?.text || reply?.label
      return !isPreambleString(label)
    })
    .slice(0, 3)
    .map((reply, index) => {
    // Handle new format: array of strings
    if (typeof reply === 'string') {
      // Smart mapping for common quick reply strings
      const lowerReply = reply.toLowerCase()
      let type: QuickReplyType = 'exploration'
      let action: 'message' | 'navigation' | 'hybrid' = 'message'
      let route: string | undefined = undefined

      if (lowerReply.includes('badge') || lowerReply.includes('earned')) {
        type = 'encouragement'
        action = 'navigation'
        route = '/rankings'
      } else if (
        lowerReply.includes('go to careers') || 
        lowerReply.includes('careers page') || 
        lowerReply.includes('view careers') ||
        lowerReply.includes('see careers') ||
        lowerReply.includes('browse careers')
      ) {
        type = 'exploration'
        action = 'navigation'
        route = '/user-careers'
      } else if (
        lowerReply.includes('advice') ||
        lowerReply.includes('expert')
      ) {
        type = 'career_action'
        action = 'message'
      } else if (
        lowerReply.includes('explore') ||
        lowerReply.includes('career')
      ) {
        type = 'exploration'
        action = 'message'
      } else if (lowerReply.includes('how') || lowerReply.includes('work')) {
        type = 'onboarding'
        action = 'message'
      }

      const mappedReply = {
        type,
        text: reply,
        action,
        priority: index + 1,
        route,
      }
      return mappedReply
    }

    // Handle old format: array of objects
    const mappedObjectReply = {
      type: mapBackendTypeToFrontend(reply.type),
      text: reply.text,
      action: mapBackendActionToFrontend(reply.action),
      priority: reply.priority || index + 1,
      route: getRouteForAction(reply.action, reply.career_id),
      career_id: reply.career_id,
    }
    return mappedObjectReply
  })
}

const Chat = () => {
  const { isChatOpen, setIsChatOpen, pendingChatMessage, setPendingChatMessage } = useLayout()
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null)
  const [selectedProvider, setSelectedProvider] = useState('claude')
  const [isLoading, setIsLoading] = useState(false)
  const [isInitializing, setIsInitializing] = useState(false)
  const [pendingMessage, setPendingMessage] = useState<ChatMessageType | null>(
    null
  )
  const [welcomeMessage, setWelcomeMessage] = useState<ChatMessageType | null>(
    null
  )
  const [currentCareerId, setCurrentCareerId] = useState<string | null>(null)
  const [latestQuickReplies, setLatestQuickReplies] = useState<any[]>([])
  const [messageFeedback, setMessageFeedback] = useState<
    Record<string, FeedbackType>
  >({})
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [openNewSessionForm, setOpenNewSessionForm] = useState(false)
  const [openChatSessions, setOpenChatSessions] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const hasInitialized = useRef(false)

  // Career context hook
  const { isOnCareerPage, careerId, careerData, userInteractionData } =
    useCareerContext()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  // Auto-close Sherpa panel only when navigating INTO routes where it crowds
  // the layout (Settings, Help Center). Tracks previous pathname so reopening
  // the panel while already on a collapse route is not immediately closed.
  const prevPathnameRef = useRef(pathname)
  useEffect(() => {
    const collapseRoutes = ['/settings', '/help-center']
    const wasInCollapse = collapseRoutes.some(p =>
      prevPathnameRef.current.startsWith(p),
    )
    const isInCollapse = collapseRoutes.some(p => pathname.startsWith(p))
    if (!wasInCollapse && isInCollapse) {
      setIsChatOpen(false)
      hasInitialized.current = false
    }
    prevPathnameRef.current = pathname
  }, [pathname, setIsChatOpen])

  // Get user info from Redux store
  const { userInfo } = useSelector((state: any) => state.auth)

  // Force OpenAI provider for users with 'user' role
  const userRole = userInfo?.user?.role
  const isAdmin = userRole === 'super'

  // Fetch available providers
  const { data: providers = [] } = useQuery({
    queryKey: ['chat-providers'],
    queryFn: getAvailableProviders,
    refetchOnWindowFocus: false,
    retry: 0,
  })

  // Ensure regular users always use OpenAI
  useEffect(() => {
    if (!isAdmin && selectedProvider !== 'openai') {
      setSelectedProvider('openai')
    }
  }, [isAdmin, selectedProvider])

  // Fetch chat sessions
  const { data: sessionsData, refetch: refetchSessions } = useQuery({
    queryKey: ['chat-sessions'],
    queryFn: getChatSessions,
    refetchOnWindowFocus: false,
    retry: 0,
  })

  // Fetch chat history for current session
  const { data: chatHistory, refetch: refetchHistory } = useQuery({
    queryKey: ['chat-history', currentSession?.session_id],
    queryFn: () => {
      if (!currentSession?.session_id) {
        throw new Error('No session ID available')
      }
      return getChatHistory(currentSession.session_id)
    },
    enabled: !!currentSession && !!currentSession.session_id,
    refetchOnWindowFocus: false,
    retry: 0,
  })

  // Mutations
  const createSessionMutation = useMutation({
    mutationFn: createChatSession,
    onSuccess: response => {
      setCurrentSession(response.session)

      // Sessions started from Home sherpa input skip welcome entirely (no fallback either)
      if (!response.welcome_message && pendingChatMessage) {
        setWelcomeMessage(null)
        setIsInitializing(false)
        refetchSessions()
        return
      }

      // Handle welcome message with personalization
      if (response.welcome_message) {
        let welcomeContent = response.welcome_message.content

        // Check if message needs personalization
        if (needsPersonalization(response.welcome_message)) {
          // Get user's first name from auth context
          const userFirstName = userInfo?.user?.profile?.first_name
          welcomeContent = personalizeWelcomeMessage(
            welcomeContent,
            userFirstName
          )
        }

        // Check for quick replies in multiple locations
        const quickRepliesFromContext =
          response.welcome_message.context?.quick_replies
        const quickRepliesFromWelcomeMessage =
          response.welcome_message.quick_replies
        const quickRepliesFromSessionRoot = response.quick_replies
        const allQuickReplies =
          quickRepliesFromContext ||
          quickRepliesFromWelcomeMessage ||
          quickRepliesFromSessionRoot

        const mappedQuickReplies: QuickReply[] | undefined = allQuickReplies
          ? mapBackendQuickRepliesToFrontend(allQuickReplies)
          : undefined

        const finalWelcomeMessage: ChatMessageType = {
          role: 'assistant',
          content: welcomeContent,
          timestamp: response.welcome_message.timestamp,
          context: null, // Reset context for display
          quick_replies: mappedQuickReplies,
          user_context_used: response.welcome_message.user_context_used,
          contextual_follow_ups: response.welcome_message.contextual_follow_ups,
        }

        setWelcomeMessage(finalWelcomeMessage)
      } else {
        // Fallback: Generate career-specific welcome message if on career page
        if (isOnCareerPage && careerData && response.session.career_id) {
          const careerWelcomeContent = generateCareerWelcomeMessage(careerData)
          setWelcomeMessage({
            role: 'assistant',
            content: careerWelcomeContent,
            timestamp: new Date().toISOString(),
            context: null,
            quick_replies: mapBackendQuickRepliesToFrontend(
              generateFallbackQuickReplies()
            ),
          })
        } else {
          // Use fallback welcome message for general chat
          const userFirstName = userInfo?.user?.profile?.first_name
          const fallbackMessage = generateFallbackWelcomeMessage(userFirstName)
          setWelcomeMessage({
            role: 'assistant',
            content: fallbackMessage,
            timestamp: new Date().toISOString(),
            context: null,
            quick_replies: mapBackendQuickRepliesToFrontend(
              generateFallbackQuickReplies()
            ),
          })
        }
      }

      refetchSessions()
      setIsInitializing(false)
      displaySuccess('New chat session created!', { autoClose: 2500, pauseOnHover: false })
    },
    onError: () => {
      displayError('Failed to create chat session')

      // Set fallback welcome message on error
      const userFirstName = userInfo?.user?.profile?.first_name
      const fallbackMessage = generateFallbackWelcomeMessage(userFirstName)
      setWelcomeMessage({
        role: 'assistant',
        content: fallbackMessage,
        timestamp: new Date().toISOString(),
        context: null,
        quick_replies: mapBackendQuickRepliesToFrontend(
          generateFallbackQuickReplies()
        ),
      })

      setIsInitializing(false)
    },
  })

  const deleteSessionMutation = useMutation({
    mutationFn: deleteChatSession,
    onSuccess: () => {
      if (currentSession) {
        setCurrentSession(null)
      }
      refetchSessions()
      displaySuccess('Chat session deleted!')
    },
    onError: () => {
      displayError('Failed to delete chat session')
    },
  })

  const sendMessageMutation = useMutation({
    mutationFn: ({
      sessionId,
      message,
      provider,
      current_career_context,
    }: {
      sessionId: string
      message: string
      provider: string
      current_career_context?: {
        career_id?: string
        career_name?: string
        career_description?: string
        user_interaction_data?: {
          is_previewed?: boolean
          is_deep_dive?: boolean
          time_spent?: number
          last_visited?: string
        }
      }
    }) =>
      sendChatMessage(sessionId, {
        message,
        provider: provider as any,
        use_rag: true,
        current_career_context,
      }),
    onSuccess: response => {
      // Process quick_replies from response and attach to the latest message
      if (response.quick_replies && response.quick_replies.length > 0) {
        // Delegate to the shared mapper, which normalizes string vs object
        // entries, filters preamble strings, and caps to 3.
        const mappedQuickReplies = mapBackendQuickRepliesToFrontend(
          response.quick_replies
        )

        // Store the quick replies to attach to the latest message
        setLatestQuickReplies(mappedQuickReplies)
      }

      refetchHistory()
      refetchSessions()
    },
    onError: () => {
      displayError('Failed to send message')
    },
  })

  // Submit feedback mutation
  const submitFeedbackMutation = useMutation({
    mutationFn: ({
      messageId,
      feedbackType,
      reason,
    }: {
      messageId: string
      feedbackType: FeedbackType
      reason: string | null
    }) => submitFeedback(messageId, feedbackType, reason),
    onSuccess: (response, variables) => {
      // Update local state
      setMessageFeedback(prev => ({
        ...prev,
        [variables.messageId]: variables.feedbackType,
      }))
      // Optional: Show brief success indicator
      if (response.is_update) {
        displaySuccess('Feedback updated')
      } else {
        displaySuccess('Feedback submitted')
      }
    },
    onError: () => {
      displayError('Failed to submit feedback')
    },
  })

  // Auto-scroll to show user message immediately, then assistant response
  useEffect(() => {
    // Scroll when user sends message (pendingMessage) or when assistant responds (!isLoading)
    if (
      pendingMessage ||
      (!isLoading && chatHistory?.messages && chatHistory.messages.length > 0)
    ) {
      messagesEndRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest', // Gentle scroll to bring into view without jumping
      })
    }
  }, [pendingMessage, isLoading, chatHistory?.messages])

  // Set default provider when providers are loaded
  useEffect(() => {
    if (providers.length > 0 && !selectedProvider) {
      const availableProvider = providers.find(p => p.available)
      if (availableProvider) {
        setSelectedProvider(availableProvider.name)
      }
    }
  }, [providers, selectedProvider])

  // Handle career changes - create new session when career changes
  const creatingForCareerId = useRef<string | null>(null)
  useEffect(() => {
    if (
      isOnCareerPage &&
      careerId &&
      careerId !== currentCareerId &&
      careerId !== creatingForCareerId.current &&
      careerData
    ) {
      setCurrentCareerId(careerId)
      creatingForCareerId.current = careerId

      // If chat is open and we have a different career, create a new session
      if (isChatOpen) {
        setIsInitializing(true)
        const topic = generateCareerSessionTopic(careerData)
        createSessionMutation.mutate({
          topic,
          career_id: careerId,
          career_context: {
            name: careerData.name,
            description: careerData.description,
            superpower: careerData.superpower,
          },
          current_career_context: {
            career_id: careerId,
            career_name: careerData.name,
            career_description: careerData.description,
            user_interaction_data: userInteractionData
              ? {
                  is_previewed: userInteractionData.is_previewed,
                  is_deep_dive: userInteractionData.is_deep_dive,
                  time_spent: userInteractionData.time_spent,
                  last_visited: userInteractionData.last_visited,
                }
              : undefined,
          },
        })
      }
    } else if (!isOnCareerPage && currentCareerId) {
      // Left career page, reset career context
      setCurrentCareerId(null)
      creatingForCareerId.current = null
    }
  }, [isOnCareerPage, careerId, currentCareerId, isChatOpen])

  // Auto-create session when chat is opened
  useEffect(() => {
    if (
      isChatOpen &&
      !currentSession &&
      !isInitializing &&
      !hasInitialized.current
    ) {
      hasInitialized.current = true
      setIsInitializing(true)

      // Create career-specific session if on career page, otherwise general session
      if (isOnCareerPage && careerData && careerId) {
        const topic = careerData.name
        createSessionMutation.mutate({
          topic,
          career_id: careerId,
          career_context: {
            name: careerData.name,
            description: careerData.description,
            superpower: careerData.superpower,
          },
          current_career_context: {
            career_id: careerId,
            career_name: careerData.name,
            career_description: careerData.description,
            user_interaction_data: userInteractionData
              ? {
                  is_previewed: userInteractionData.is_previewed,
                  is_deep_dive: userInteractionData.is_deep_dive,
                  time_spent: userInteractionData.time_spent,
                  last_visited: userInteractionData.last_visited,
                }
              : undefined,
          },
        })
      } else {
        // Generate topic with today's date for general chat
        const today = new Date()
        const topic = `New Chat ${
          today.getMonth() + 1
        }/${today.getDate()}/${today.getFullYear()}`
        createSessionMutation.mutate({
          topic,
          current_career_context: {}, // Empty context for general chat
          skip_welcome: !!pendingChatMessage, // Skip welcome when started from Home sherpa input
        })
      }
    }
  }, [
    isChatOpen,
    currentSession,
    isInitializing,
    isOnCareerPage,
    careerData,
    careerId,
  ])

  // Load feedback when chat history changes
  useEffect(() => {
    if (chatHistory?.messages) {
      const assistantMessages = chatHistory.messages.filter(
        msg => msg.role === 'assistant' && msg.id
      )

      if (assistantMessages.length > 0) {
        const messageIds = assistantMessages
          .map(msg => msg.id?.toString())
          .filter(Boolean) as string[]

        // Load feedback for all assistant messages
        getBatchFeedback(messageIds)
          .then(feedbackMap => {
            setMessageFeedback(feedbackMap)
          })
          .catch(error => {
            console.error('Failed to load feedback:', error)
          })
      }
    }
  }, [chatHistory?.messages])

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  // Handle feedback submission
  const handleFeedbackChange = async (
    messageId: string,
    feedbackType: FeedbackType,
    reason: string | null
  ) => {
    await submitFeedbackMutation.mutateAsync({
      messageId,
      feedbackType,
      reason: reason || null,
    })
  }

  const handleCreateSession = (topic: string) => {
    // Generate topic based on context if not provided
    let finalTopic = topic
    if (!topic || topic.trim() === '') {
      if (isOnCareerPage && careerData) {
        finalTopic = careerData.name
      } else {
        // Generate topic with today's date for general chat
        const today = new Date()
        finalTopic = `New Chat ${
          today.getMonth() + 1
        }/${today.getDate()}/${today.getFullYear()}`
      }
    }

    // Include career context if on career page
    const sessionData =
      isOnCareerPage && careerData && careerId
        ? {
            topic: finalTopic,
            career_id: careerId,
            career_context: {
              name: careerData.name,
              description: careerData.description,
              superpower: careerData.superpower,
            },
            current_career_context: {
              career_id: careerId,
              career_name: careerData.name,
              career_description: careerData.description,
              user_interaction_data: userInteractionData
                ? {
                    is_previewed: userInteractionData.is_previewed,
                    is_deep_dive: userInteractionData.is_deep_dive,
                    time_spent: userInteractionData.time_spent,
                    last_visited: userInteractionData.last_visited,
                  }
                : undefined,
            },
          }
        : {
            topic: finalTopic,
            current_career_context: {}, // Empty context for general chat
          }

    createSessionMutation.mutate(sessionData)
    setOpenNewSessionForm(false)
    setOpenChatSessions(false)
  }

  const handleDeleteSession = (sessionId: string) => {
    deleteSessionMutation.mutate(sessionId)
  }

  const handleSendMessage = (
    message: string,
    callbacks?: { onSent?: () => void },
  ) => {
    if (!currentSession || !currentSession.session_id) {
      displayError('Please wait while we set up your chat session')
      return
    }

    // Create optimistic user message
    const optimisticMessage: ChatMessageType = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
      context: null,
    }
    setPendingMessage(optimisticMessage)

    // Clear previous quick replies when sending a new message
    setLatestQuickReplies([])

    setIsLoading(true)

    // Prepare enhanced career context for message if available
    const currentCareerContext =
      isOnCareerPage && careerData && careerId
        ? {
            career_id: careerId,
            career_name: careerData.name,
            career_description: careerData.description,
            user_interaction_data: userInteractionData
              ? {
                  is_previewed: userInteractionData.is_previewed,
                  is_deep_dive: userInteractionData.is_deep_dive,
                  time_spent: userInteractionData.time_spent,
                  last_visited: userInteractionData.last_visited,
                }
              : undefined,
          }
        : undefined

    sendMessageMutation.mutate(
      {
        sessionId: currentSession.session_id,
        message,
        provider: selectedProvider,
        current_career_context: currentCareerContext,
      },
      {
        onSuccess: () => {
          setPendingMessage(null)
          callbacks?.onSent?.()
        },
        onError: () => {
          setPendingMessage(null)
        },
        onSettled: () => {
          setIsLoading(false)
        },
      }
    )
  }

  const handleQuickReply = (message: string) => {
    handleSendMessage(message)
  }

  // Auto-send preloaded message once session is ready (e.g. from Home sherpa
  // search). Track the last attempt via ref so a failed send doesn't loop, and
  // only clear the queued message after the send actually succeeds — otherwise
  // a transient network error would silently drop the user's prompt.
  const autoSentChatMsgRef = useRef<string | null>(null)
  useEffect(() => {
    if (
      isChatOpen &&
      pendingChatMessage &&
      currentSession?.session_id &&
      !sendMessageMutation.isPending &&
      autoSentChatMsgRef.current !== pendingChatMessage
    ) {
      autoSentChatMsgRef.current = pendingChatMessage
      handleSendMessage(pendingChatMessage, {
        onSent: () => {
          setPendingChatMessage(null)
          autoSentChatMsgRef.current = null
        },
      })
    }
  }, [isChatOpen, pendingChatMessage, currentSession?.session_id])

  const handleHybridAction = (message: string, route: string) => {
    // Send message first
    handleSendMessage(message)
    // Navigate after a short delay to allow message to be sent
    setTimeout(() => {
      navigate(route)
    }, 1000)
  }

  const handleSessionSelect = (session: ChatSession) => {
    setCurrentSession(session)
    setWelcomeMessage(null) // Clear welcome message when switching sessions
    setOpenNewSessionForm(false)
    setOpenChatSessions(false)
  }

  const handleCloseChat = () => {
    setIsChatOpen(false)
    hasInitialized.current = false
  }

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  const toggleSessionManager = () => {
    setIsDropdownOpen(false)
    setOpenNewSessionForm(false)
    setOpenChatSessions(false)
  }

  const formatMessages = (messages: ChatMessageType[]) => {
    const formattedMessages: Array<{
      type: 'user' | 'ai' | 'suggestions'
      message?: ChatMessageType
      quickReplies?: any[]
    }> = []

    // Handle welcome message and its quick replies
    if (welcomeMessage) {
      // Always add the welcome message first with feedback data
      const welcomeMessageWithFeedback = {
        ...welcomeMessage,
        feedback: welcomeMessage.id
          ? messageFeedback[welcomeMessage.id.toString()] || null
          : null,
        isWelcomeMessage: true,
      }
      formattedMessages.push({
        type: 'ai',
        message: welcomeMessageWithFeedback,
      })

      // Always add quick replies immediately after the welcome message
      if (
        welcomeMessage.quick_replies &&
        welcomeMessage.quick_replies.length > 0
      ) {
        formattedMessages.push({
          type: 'suggestions',
          quickReplies: welcomeMessage.quick_replies,
        })
      }
    }

    // Add existing messages, but filter out the welcome message if we have one
    messages.forEach((msg, index) => {
      // Skip the welcome message if we have one in state (to avoid duplication)
      if (
        welcomeMessage &&
        msg.role === 'assistant' &&
        msg.content === welcomeMessage.content
      ) {
        return
      }

      if (msg.role === 'user') {
        formattedMessages.push({
          type: 'user',
          message: msg,
        })
      } else if (msg.role === 'assistant') {
        // Add the assistant message with feedback data
        const messageWithFeedback = {
          ...msg,
          feedback: msg.id ? messageFeedback[msg.id.toString()] || null : null,
        }
        formattedMessages.push({
          type: 'ai',
          message: messageWithFeedback,
        })

        // Check if this is the latest assistant message and we have quick replies
        const isLatestAssistantMessage =
          index === messages.length - 1 && latestQuickReplies.length > 0

        // Add suggestions as a separate message if available.
        // History-loaded msg.quick_replies are raw backend payloads — must be
        // mapped to FE format so click handlers (action: message|navigation|
        // hybrid) actually fire.
        if (isLatestAssistantMessage || msg.quick_replies) {
          const quickReplies = isLatestAssistantMessage
            ? latestQuickReplies
            : mapBackendQuickRepliesToFrontend(msg.quick_replies as any[])
          if (quickReplies && quickReplies.length > 0) {
            formattedMessages.push({
              type: 'suggestions',
              quickReplies: quickReplies,
            })
          }
        }
      }
    })

    // Add pending message if exists
    if (pendingMessage) {
      formattedMessages.push({
        type: 'user',
        message: pendingMessage,
      })
    }

    return formattedMessages
  }

  return (
    <>
      {/* Session Manager */}
      <div
        className={`flex flex-col fixed top-0 w-full sm:w-[404px] h-screen bg-white border-l border-border z-50 shadow-2xl transition-transform duration-300 ease-in-out ${
          openNewSessionForm || openChatSessions
            ? 'right-0 translate-x-0'
            : 'right-0 translate-x-full'
        }`}
      >
        <ChatSessionManager
          sessions={sessionsData?.sessions || []}
          currentSession={currentSession}
          onSessionSelect={handleSessionSelect}
          onCreateSession={handleCreateSession}
          onDeleteSession={handleDeleteSession}
          onClose={toggleSessionManager}
          openNewSessionForm={openNewSessionForm}
          openChatSessions={openChatSessions}
          isLoading={
            createSessionMutation.isPending || deleteSessionMutation.isPending
          }
        />
      </div>
      <AnimatePresence>
        {isChatOpen && (
          <>
            <ChatWalkthrough />
            <motion.aside
            className="flex flex-col fixed top-0 right-0 w-full sm:w-[404px] h-screen bg-white border-l border-border z-40"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', mass: 1, stiffness: 80, damping: 14 }}
          >
            {/* Header */}
            <div className="h-[81px] px-8 flex items-center justify-between border-b border-[#E8E8E8]">
                <div className="flex items-center gap-1.5">
                  <h2 className="text-2xl font-semibold capitalize text-brand">
                    Career Sherpa
                  </h2>
                  <div className="relative" ref={dropdownRef}>
                    <span
                      className="cursor-pointer hover:bg-border rounded-full p-1 transition-colors duration-150 inline-flex items-center justify-center"
                      onClick={handleDropdownToggle}
                    >
                      <img
                        src="/assets/svg/dots.svg"
                        alt="dots"
                        width={16}
                        height={16}
                      />
                    </span>

                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 bg-white rounded-lg border border-border shadow-subtle z-10 min-w-[168px] overflow-hidden p-1.5">
                        <button
                          className="w-full text-left px-3 py-2 rounded-md text-xs font-normal text-text-heading hover:bg-surface-muted transition-all duration-150 block border-none outline-none"
                          onClick={() => {
                            setOpenChatSessions(true)
                            setIsDropdownOpen(false)
                          }}
                        >
                          View chat sessions
                        </button>
                        <button
                          className="w-full text-left px-3 py-2 rounded-md text-xs font-normal text-text-heading hover:bg-surface-muted transition-all duration-150 block border-none outline-none"
                          onClick={() => {
                            setOpenNewSessionForm(true)
                            setIsDropdownOpen(false)
                          }}
                        >
                          New chat
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center">
                  <button
                    className="cursor-pointer p-1 hover:bg-border rounded-full"
                    onClick={handleCloseChat}
                    title="Close chat"
                  >
                    <svg
                      className="w-5 h-5 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
            </div>

            {/* Messages */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {isInitializing ? (
                <div className="flex-1 flex items-center justify-center p-4">
                  <div className="text-center text-text-muted">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action mx-auto mb-2"></div>
                    <p>Setting up your chat session...</p>
                  </div>
                </div>
              ) : currentSession ? (
                <>
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 scroll-smooth">
                    {(() => {
                      const formattedMessages = formatMessages(
                        chatHistory?.messages || []
                      )

                      if (formattedMessages.length > 0) {
                        return formattedMessages.map((item, index) => {
                          if (item.type === 'user') {
                            return (
                              <div key={index}>
                                <UserMessage
                                  message={item.message!}
                                  showTimestamp={true}
                                />
                              </div>
                            )
                          } else if (item.type === 'suggestions') {
                            return (
                              <div key={index}>
                                <SuggestionPillsMessage
                                  quickReplies={item.quickReplies!}
                                  onMessageReply={handleQuickReply}
                                  onHybridAction={handleHybridAction}
                                  disabled={
                                    sendMessageMutation.isPending || isLoading
                                  }
                                />
                              </div>
                            )
                          } else {
                            return (
                              <div key={index}>
                                <ChatMessage
                                  message={item.message!}
                                  showProvider={true}
                                  showContext={true}
                                  onFeedbackChange={handleFeedbackChange}
                                />
                              </div>
                            )
                          }
                        })
                      } else {
                        return (
                          <div className="text-center py-8">
                            <div className="mb-4">
                              <svg
                                className="w-12 h-12 mx-auto text-border"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1}
                                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                />
                              </svg>
                            </div>
                            <p className="text-lg font-semibold text-brand mb-2">
                              Welcome to Career Sherpa!
                            </p>
                            <p className="text-sm text-text-muted">
                              Start a conversation with Career Sherpa. You can
                              ask questions about careers, get advice, or
                              explore different topics.
                            </p>
                          </div>
                        )
                      }
                    })()}
                    {isLoading && <ChatIsTyping isTyping={true} />}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Chat Toolbar */}
                  <div className="p-4">
                    <ChatToolbar
                      onSendMessage={handleSendMessage}
                      providers={providers}
                      selectedProvider={selectedProvider}
                      onProviderChange={setSelectedProvider}
                      isLoading={isLoading}
                      disabled={sendMessageMutation.isPending}
                      userRole={userRole}
                    />
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center p-4">
                  <div className="text-center text-text-muted">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action mx-auto mb-2"></div>
                    <p>Loading chat session...</p>
                  </div>
                </div>
              )}
            </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  )
}

export default Chat
